import CloseIcon from "@mui/icons-material/Close";
import { AppBar, Box, Button, Dialog, IconButton, Slide, Toolbar, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { TransitionProps } from "@mui/material/transitions";
import { DateTime } from "luxon";
import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthProvider";
import { type HospitalDataContextType, useHospitalData } from "../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useBedsideNurse } from "../../pages/BedsideHome/hooks";
import { INK, INK_BLACK, NEUTRAL, SURFACE } from "../../theme/tokens";
import { trackErrorEvent, trackEvent } from "../../utils/analytics";
import { NurseFullRole, type SelectedShift, ShiftType } from "../../utils/models";
import axiosInstance from "../../utils/networkWrapper";
import { CustomCalendar } from "../CustomCalendar/CustomCalendar";
import { usePerformGlobalRefresh } from "../header/hooks";

const Transition = React.forwardRef(function Transition(
	props: TransitionProps & {
		children: React.ReactElement;
	},
	ref: React.Ref<unknown>,
) {
	return <Slide direction="up" ref={ref} {...props} />;
});

interface AskForReplacementDialogProps {
	open: boolean;
	onClose: () => void;
}

export const AskForReplacementDialog = ({ open, onClose }: AskForReplacementDialogProps) => {
	const [selectedDate, setSelectedDate] = useState<DateTime>(DateTime.now());
	const [selectedShift, setSelectedShift] = useState<SelectedShift | null>(null);
	const { hospitalData, chosenDepartment, calculateShiftTimes, timezone, departmentNurseTypes, showErrorSnackbar, isSnf }: HospitalDataContextType =
		useHospitalData();
	const { userShifts } = useBedsideNurse();
	const { userInfo } = useAuth();
	const { showSnackbar } = useSnackbar();
	const [isLoading, setIsLoading] = useState(false);
	const { t } = useTranslation();
	const { performGlobalRefresh } = usePerformGlobalRefresh({});

	// Track dialog opened
	React.useEffect(() => {
		if (open) {
			trackEvent("ask_for_replacement_dialog_opened", {
				department_id: chosenDepartment?.department_id,
				nurse_id: userInfo?.id,
			});
		}
	}, [open, chosenDepartment?.department_id, userInfo?.id]);

	const handleDateSelect = (date: DateTime) => {
		setSelectedDate(date);
		trackEvent("ask_for_replacement_dialog_date_selected", {
			department_id: chosenDepartment?.department_id,
			nurse_id: userInfo?.id,
			date: date.toFormat("yyyy-MM-dd"),
		});
	};

	const handleCalendarShiftInfo = (selectedShift: SelectedShift) => {
		setSelectedShift(selectedShift);

		trackEvent("ask_for_replacement_dialog_shift_selected", {
			department_id: chosenDepartment?.department_id,
			nurse_id: userInfo?.id,
			date: selectedShift.date,
			shift_type: selectedShift.shiftType || "none",
			has_start_time: !!selectedShift.startTime,
			has_end_time: !!selectedShift.endTime,
		});
	};

	const handleSendRequest = async () => {
		if (!selectedShift?.shiftType || !userInfo?.id || !chosenDepartment?.department_id) return;

		trackEvent("ask_for_replacement_dialog_send_request", {
			department_id: chosenDepartment?.department_id,
			nurse_id: userInfo?.id,
			date: selectedDate.toFormat("yyyy-MM-dd"),
			shift_type: selectedShift.shiftType,
		});

		setIsLoading(true);
		const nurse = hospitalData?.nurses.find((n: any) => n.userId === userInfo.id);
		const role = nurse?.role;

		const shiftDate = selectedDate;
		const today = DateTime.now();
		const dayNumber = shiftDate.diff(today, "day");
		const shiftNo = selectedShift.shiftType === ShiftType.Night ? 1 : 0;

		// Calculate shift times using the utility function
		const { startTime: defaultStartTime, endTime: defaultEndTime } = calculateShiftTimes({ date: shiftDate }, shiftNo, role);

		let roles = [role];
		const hasLPN = departmentNurseTypes.includes(NurseFullRole.LPN);
		if (isSnf && (role === NurseFullRole.RN || role === NurseFullRole.LPN || role === NurseFullRole.LVN)) {
			roles = [NurseFullRole.RN, hasLPN ? NurseFullRole.LPN : NurseFullRole.LVN];
		}

		try {
			const response = await axiosInstance.post(
				"/staffing/replace-shift",
				{
					day_number: dayNumber,
					shift_type: shiftNo === 0 ? ShiftType.Day : ShiftType.Night,
					roles: roles,
					created_by: String(userInfo.id),
					shift_start: selectedShift.startTime instanceof DateTime ? selectedShift.startTime.toISO() : defaultStartTime.toISO(),
					shift_end: selectedShift.endTime instanceof DateTime ? selectedShift.endTime.toISO() : defaultEndTime.toISO(),
					replacement_nurse_id: nurse?.id,
					replacement_shift_id: selectedShift.shiftId,
					job_id: userShifts.find((s) => s.id === selectedShift.shiftId)?.jobId,
				},
				{
					params: {
						department_id: chosenDepartment.department_id,
					},
				},
			);

			if (response.status === 200) {
				showSnackbar(t("snakebar.shift.replace.success"), "success");
				trackEvent("ask_for_replacement_dialog_request_success", {
					department_id: chosenDepartment?.department_id,
					nurse_id: userInfo?.id,
					shift_start: selectedShift?.startTime?.setZone(timezone)?.toISO(),
					shift_end: selectedShift?.endTime?.setZone(timezone)?.toISO(),
					shift_type: shiftNo === 0 ? ShiftType.Day : ShiftType.Night,
				});
				await performGlobalRefresh({
					avoidRefreshFor: {
						nursesData: true,
						notificationsData: true,
						departmentData: true,
					},
				});
				onClose();
			} else {
				throw new Error("Failed to create open shift");
			}
		} catch (error: unknown) {
			showErrorSnackbar(
				t("snakebar.shift.replace.error"),
				{
					action: "request swap failed",
					shift_start: selectedShift?.startTime?.setZone(timezone)?.toISO(),
					shift_end: selectedShift?.endTime?.setZone(timezone)?.toISO(),
					shift_type: shiftNo === 0 ? ShiftType.Day : ShiftType.Night,
				},
				error,
			);
			trackErrorEvent("ask_for_replacement_dialog_request_failed", error, {
				department_id: chosenDepartment?.department_id,
				nurse_id: userInfo?.id,
				shift_start: selectedShift?.startTime?.setZone(timezone)?.toISO(),
				shift_end: selectedShift?.endTime?.setZone(timezone)?.toISO(),
				shift_type: shiftNo === 0 ? ShiftType.Day : ShiftType.Night,
			});
		} finally {
			setIsLoading(false);
		}
	};

	const renderShiftInfo = () => {
		if (!selectedShift?.shiftType) {
			return (
				<Typography
					sx={{
						fontSize: "0.875rem",
						color: INK.SECONDARY,
						fontWeight: 600,
					}}
				>
					{t("no.shift.on.day")}
				</Typography>
			);
		}

		const isNightShift = selectedShift.shiftType === ShiftType.Night;
		const isDayShift = selectedShift.shiftType === ShiftType.Day;

		return (
			<>
				{isNightShift && (
					<Box
						sx={{
							width: 14,
							height: 14,
							display: "flex",
							alignItems: "center",
						}}
					>
						<img src="/moon.svg" alt={t("night.shift")} style={{ width: "14px", height: "14px" }} />
					</Box>
				)}
				{isDayShift && (
					<Box
						sx={{
							width: 14,
							height: 14,
							display: "flex",
							alignItems: "center",
						}}
					>
						<img src="/sun.svg" alt={t("day.shift")} style={{ width: "14px", height: "14px" }} />
					</Box>
				)}
				<Typography
					sx={{
						fontSize: "0.875rem",
						color: INK.SECONDARY,
						fontWeight: 600,
					}}
				>
					{isNightShift ? t("night.shift") : t("day.shift")}
				</Typography>
			</>
		);
	};

	return (
		<Dialog
			fullScreen
			open={open}
			onClose={() => {
				trackEvent("ask_for_replacement_dialog_closed", {
					department_id: chosenDepartment?.department_id,
					nurse_id: userInfo?.id,
				});
				onClose();
			}}
			data-testid="ask-replacement-dialog"
			slots={{
				transition: Transition,
			}}
		>
			<AppBar
				sx={{
					position: "relative",
					bgcolor: SURFACE,
					color: INK_BLACK,
					boxShadow: "none",
				}}
			>
				<Toolbar sx={{ minHeight: "56px !important" }}>
					<IconButton
						edge="start"
						color="inherit"
						onClick={() => {
							trackEvent("ask_for_replacement_dialog_close_button_clicked");
							onClose();
						}}
						aria-label="close"
						sx={{ mr: 1 }}
						data-testid="ask-replacement-close-button"
					>
						<CloseIcon />
					</IconButton>
					<Typography variant="h6" component="div" sx={{ fontSize: "1rem" }}>
						{t("ask.for.replacement")}
					</Typography>
				</Toolbar>
			</AppBar>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					height: "calc(100% - 56px)",
					bgcolor: SURFACE,
				}}
			>
				<Box sx={{ flex: 1, overflow: "auto" }}>
					<Box sx={{ p: 2 }}>
						<CustomCalendar
							shifts={userShifts}
							value={selectedDate}
							onChange={handleDateSelect}
							onShiftInfo={handleCalendarShiftInfo}
							isOpen={true}
							showOneWeek={false}
						/>

						{selectedShift && (
							<Box
								sx={{
									bgcolor: NEUTRAL[50],
									borderRadius: "8px",
									p: 2,
									mt: 2,
								}}
							>
								<Typography
									variant="subtitle2"
									sx={{
										color: alpha(INK.SECONDARY, 0.5),
										fontWeight: 600,
										fontSize: "0.875rem",
										mb: 1,
									}}
								>
									{t("selected.shift")}
								</Typography>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
									}}
								>
									<Typography
										sx={{
											fontSize: "0.875rem",
											fontWeight: 600,
											color: INK.SECONDARY,
										}}
									>
										{selectedDate.toFormat("ccc, MMM D, yyyy")}
									</Typography>
									<span style={{ fontSize: 20, color: INK.SECONDARY }}> | </span>
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 0.5,
											fontWeight: 600,
											color: INK.SECONDARY,
										}}
									>
										{renderShiftInfo()}
									</Box>
								</Box>
							</Box>
						)}
					</Box>
				</Box>

				<Box
					sx={{
						p: 2,
						borderTop: `1px solid ${NEUTRAL[300]}`,
						bgcolor: SURFACE,
					}}
				>
					<Button
						variant="contained"
						onClick={() => {
							trackEvent("ask_for_replacement_dialog_cancelled", {
								department_id: chosenDepartment?.department_id,
								nurse_id: userInfo?.id,
							});
							onClose();
						}}
						sx={{ mr: 1 }}
						data-testid="ask-replacement-cancel-button"
					>
						{t("cancel")}
					</Button>
					<Button variant="contained" onClick={handleSendRequest} disabled={!selectedShift?.shiftType || isLoading} data-testid="ask-replacement-submit-button">
						{isLoading ? t("sending") : t("send.request")}
					</Button>
				</Box>
			</Box>
		</Dialog>
	);
};
